/**
 * Site Page Template Registry
 *
 * Templates are aligned 1:1 with actual .tsx page components.
 * Section IDs and field keys match what parseSitePageContent() in
 * Landing.tsx reads from content.settings.sections.
 *
 * Tenant-aware: Landing template surfaces the correct sections per
 * deployment (Honeyshares vs REDEX).
 */

import {
  Home, Info, Users, Megaphone, FileText, Star, Mail,
  HelpCircle, Layers, Building2, Shield, CreditCard,
  Receipt, Globe, Phone, type LucideIcon,
} from 'lucide-react';
import { TENANT } from '../../../config';

/* ─── Types ───────────────────────────────────────────────────── */

export type SiteFieldType = 'text' | 'textarea' | 'url' | 'image' | 'richlist' | 'faq' | 'links' | 'teamMembers' | 'stats' | 'features';

export interface SiteField {
  key: string;
  label: string;
  type: SiteFieldType;
  placeholder?: string;
}

export interface SiteSection {
  id: string;
  label: string;
  icon: LucideIcon;
  fields: SiteField[];
}

export interface SiteTemplate {
  id: string;
  label: string;
  description: string;
  icon: LucideIcon;
  sections: SiteSection[];
  defaults: Record<string, Record<string, any>>;
}

/* ─── Shared ──────────────────────────────────────────────────── */

const NAV_SECTION: SiteSection = {
  id: 'nav',
  label: 'Navigation',
  icon: Layers,
  fields: [
    { key: 'links', label: 'Nav links', type: 'links' },
  ],
};

const FOOTER_SECTION: SiteSection = {
  id: 'footer',
  label: 'Footer',
  icon: FileText,
  fields: [
    { key: 'disclaimer', label: 'Legal disclaimer', type: 'textarea', placeholder: 'Investing involves risk...' },
  ],
};

const NEWSLETTER_SECTION: SiteSection = {
  id: 'newsletter',
  label: 'Newsletter',
  icon: Mail,
  fields: [
    { key: 'heading', label: 'Heading', type: 'text', placeholder: 'Stay Updated' },
    { key: 'subheading', label: 'Subheading', type: 'textarea', placeholder: 'Get the latest news...' },
  ],
};

/* ═══════════════════════════════════════════════════════════════
 *  HERO section — shared between both landing variants
 * ═══════════════════════════════════════════════════════════════ */

const HERO_SECTION: SiteSection = {
  id: 'hero',
  label: 'Hero Banner',
  icon: Star,
  fields: [
    { key: 'heading', label: 'Heading', type: 'text', placeholder: 'Page Title' },
    { key: 'subheading', label: 'Subheading', type: 'textarea', placeholder: 'Subtitle or tagline' },
    { key: 'body', label: 'Body text', type: 'textarea', placeholder: 'Extended description' },
    { key: 'ctaText', label: 'CTA button text', type: 'text', placeholder: 'Get Started' },
    { key: 'ctaUrl', label: 'CTA button URL', type: 'url', placeholder: '/register' },
    { key: 'secondaryCtaText', label: 'Secondary CTA text', type: 'text', placeholder: 'Learn More' },
    { key: 'secondaryCtaUrl', label: 'Secondary CTA URL', type: 'url', placeholder: '/demo' },
    { key: 'backgroundImage', label: 'Background image', type: 'image' },
  ],
};

/* ═══════════════════════════════════════════════════════════════
 *  HONEYSHARES LANDING sections
 * ═══════════════════════════════════════════════════════════════ */

const HS_HOW_IT_WORKS: SiteSection = {
  id: 'howItWorks',
  label: 'How It Works',
  icon: Layers,
  fields: [
    { key: 'heading', label: 'Section heading', type: 'text', placeholder: 'How It Works' },
    { key: 'steps', label: 'Steps', type: 'richlist' },
  ],
};

const HS_HIVE_FUND: SiteSection = {
  id: 'hiveFund',
  label: 'The Hive Fund',
  icon: Building2,
  fields: [
    { key: 'heading', label: 'Heading', type: 'text', placeholder: 'The Hive Fund' },
    { key: 'targetIrr', label: 'Target IRR', type: 'text', placeholder: '18%+' },
    { key: 'targetCashflow', label: 'Target Cashflow', type: 'text', placeholder: '12%+' },
    { key: 'minimumInvestment', label: 'Minimum investment', type: 'text', placeholder: '$10,000' },
    { key: 'reserves', label: 'Reserves note', type: 'text', placeholder: '6 months of reserves' },
    { key: 'image', label: 'Feature image', type: 'image' },
    { key: 'ctaText', label: 'CTA button', type: 'text', placeholder: 'Request Access' },
    { key: 'secondaryCtaText', label: 'Secondary CTA', type: 'text', placeholder: 'Learn More' },
  ],
};

const HS_FAQ: SiteSection = {
  id: 'faq',
  label: 'FAQ',
  icon: HelpCircle,
  fields: [
    { key: 'heading', label: 'Section heading', type: 'text', placeholder: 'Frequently Asked Questions' },
    { key: 'items', label: 'FAQ items', type: 'faq' },
  ],
};

/* ═══════════════════════════════════════════════════════════════
 *  REDEX LANDING sections
 * ═══════════════════════════════════════════════════════════════ */

const RX_PLATFORM: SiteSection = {
  id: 'platform',
  label: 'Platform',
  icon: Shield,
  fields: [
    { key: 'heading', label: 'Heading', type: 'text', placeholder: 'Capital sovereignty for builders' },
    { key: 'subheading', label: 'Subheading', type: 'textarea', placeholder: 'REDEX exists to restore capital sovereignty...' },
    { key: 'pillars', label: 'Platform pillars', type: 'features' },
    { key: 'builtForTags', label: 'Built-for tags (comma-separated)', type: 'textarea', placeholder: 'Commercial operators, Acquisition vehicles, Private funds' },
  ],
};

const RX_RAILS: SiteSection = {
  id: 'rails',
  label: 'Rails',
  icon: CreditCard,
  fields: [
    { key: 'heading', label: 'Heading', type: 'text', placeholder: 'Programmable capital rails' },
    { key: 'body', label: 'Body', type: 'textarea', placeholder: 'Infrastructure-first banking alignment...' },
    { key: 'cards', label: 'Rail cards', type: 'features' },
  ],
};

const RX_INVOICES: SiteSection = {
  id: 'invoices',
  label: 'Invoices',
  icon: Receipt,
  fields: [
    { key: 'heading', label: 'Heading', type: 'text', placeholder: 'Transaction + invoicing infrastructure' },
    { key: 'subheading', label: 'Subheading', type: 'textarea', placeholder: 'API-connected invoicing...' },
    { key: 'cards', label: 'Invoice cards', type: 'features' },
  ],
};

/* ═══════════════════════════════════════════════════════════════
 *  LANDING TEMPLATE — tenant-aware
 * ═══════════════════════════════════════════════════════════════ */

const isRedex = TENANT.id === 'redex';

const LANDING_TEMPLATE: SiteTemplate = {
  id: 'landing',
  label: 'Landing Page',
  description: isRedex
    ? 'REDEX homepage: hero, platform, rails, invoices, contact'
    : 'Homepage: hero, how it works, hive fund, FAQ, newsletter',
  icon: Home,
  sections: isRedex
    ? [NAV_SECTION, HERO_SECTION, RX_PLATFORM, RX_RAILS, RX_INVOICES, FOOTER_SECTION]
    : [NAV_SECTION, HERO_SECTION, HS_HOW_IT_WORKS, HS_HIVE_FUND, HS_FAQ, NEWSLETTER_SECTION, FOOTER_SECTION],
  defaults: isRedex
    ? {
        hero: {
          heading: 'American Dynamism Unleashed',
          subheading: 'Capital infrastructure for builders, operators, and owners shaping the next generation of industry.',
          body: 'We believe ownership is the foundation of sovereignty, opportunity, and long-term prosperity. REDEX builds programmable capital rails that allow sovereign operators to structure, raise, and manage capital under their own brand \u2014 without marketplace extraction or platform dependency.',
          ctaText: 'REQUEST ACCESS',
          ctaUrl: '/register',
          secondaryCtaText: 'SEE THE PLATFORM',
          secondaryCtaUrl: '/demo',
        },
        platform: {
          heading: 'Capital sovereignty for builders',
          subheading: 'REDEX exists to restore capital sovereignty to builders \u2014 infrastructure that lets operators raise and manage capital under their own brand.',
          pillars: [
            { title: 'Sovereignty', description: 'Operators control the capital stack. No marketplace extraction. No platform dependency.' },
            { title: 'Ownership', description: 'Everything centers around equity and structured ownership \u2014 not token hype.' },
            { title: 'Industrialization', description: 'Hard assets, real businesses, real infrastructure \u2014 built for operators.' },
            { title: 'Opportunity', description: 'Capital formation for builders and operators \u2014 aligned with real economy workflows.' },
          ],
          builtForTags: 'Commercial operators, Acquisition vehicles, Private funds, Hard assets, R&D capital formation, Structured ownership',
        },
        rails: {
          heading: 'Programmable capital rails',
          body: 'Infrastructure-first banking alignment: KYC gates, domestic settlement, and modern ownership workflows \u2014 delivered under your brand.',
          cards: [
            { title: 'White-labeled portals', description: 'A fully white-labeled investor portal for operators raising under their own brand.' },
            { title: 'Bank-grade ACH rails', description: 'Plaid + ACH for payables, distributions, and treasury movement.' },
            { title: 'KYC-gated onboarding', description: 'Compliance gates that protect offerings and counterparties \u2014 without user friction.' },
            { title: 'Crypto-enabled settlement (optional)', description: 'Use on-chain settlement where it fits \u2014 designed for execution, not speculation.' },
          ],
        },
        invoices: {
          heading: 'Transaction + invoicing infrastructure',
          subheading: 'API-connected invoicing and auditable execution flows \u2014 designed to integrate with real operators and real balance sheets.',
          cards: [
            { title: 'Structured invoice workflows', description: 'Represent obligations as trackable objects with controlled access and lifecycle state.' },
            { title: 'Approvals, audit, and reporting', description: 'Capture approvals, timestamps, and settlement events end-to-end for clean reporting.' },
          ],
        },
        footer: {
          disclaimer: 'Platform usage involves risk, including loss of funds and operational errors. Past performance does not guarantee future results.',
        },
      }
    : {
        hero: {
          heading: 'Real Estate Investing, Simplified',
          subheading: 'Institutional-grade real estate, simplified for the modern investor',
          ctaText: 'Get Started',
          ctaUrl: '/register',
        },
        howItWorks: {
          heading: 'How It Works',
          steps: [
            { title: 'Browse', description: 'Explore our curated selection of institutional-grade real estate investments' },
            { title: 'Invest', description: 'Choose properties that align with your investment goals and risk tolerance' },
            { title: 'Earn', description: 'Receive regular distributions and benefit from property appreciation' },
          ],
        },
        hiveFund: {
          heading: 'The Hive Fund',
          targetIrr: '18%+',
          targetCashflow: '12%+',
          minimumInvestment: '$10,000',
          reserves: '6 months of reserves',
          ctaText: 'Request Access',
          secondaryCtaText: 'Learn More',
        },
        faq: {
          heading: 'Frequently Asked Questions',
          items: [
            { question: 'How does investing with Honeyshares work?', answer: 'Eligible investors can access curated property-backed opportunities with professional management and investor reporting.' },
            { question: 'How do I earn returns?', answer: 'You earn returns through regular dividend payments from rental income and capital appreciation when property values increase.' },
            { question: 'Is my investment secure?', answer: 'We use industry-standard safeguards across accounts and data flows. All investments involve risk, including possible loss of principal.' },
          ],
        },
        newsletter: {
          heading: 'Stay Updated',
          subheading: 'Get the latest investment opportunities and market insights delivered to your inbox',
        },
        footer: {
          disclaimer: 'Investing in real estate involves risk, including loss of principal. Past performance does not guarantee future results.',
        },
      },
};

/* ═══════════════════════════════════════════════════════════════
 *  ABOUT PAGE
 * ═══════════════════════════════════════════════════════════════ */

const ABOUT_TEMPLATE: SiteTemplate = {
  id: 'about',
  label: 'About Us',
  description: 'Company story, mission, strategy, and feature cards',
  icon: Info,
  sections: [
    NAV_SECTION,
    { id: 'hero', label: 'Hero', icon: Star, fields: [
      { key: 'heading', label: 'Heading', type: 'text', placeholder: 'About Honeyshares' },
      { key: 'body', label: 'Body (multiple paragraphs)', type: 'textarea', placeholder: 'Real estate has long been...' },
      { key: 'ctaText', label: 'CTA button text', type: 'text', placeholder: 'Get Started' },
      { key: 'ctaUrl', label: 'CTA URL', type: 'url', placeholder: '/register' },
      { key: 'image', label: 'Hero image', type: 'image' },
    ]},
    { id: 'empowering', label: 'Empowering Investors', icon: Shield, fields: [
      { key: 'heading', label: 'Heading', type: 'text', placeholder: 'Empowering investors' },
      { key: 'statements', label: 'Mission statements', type: 'richlist' },
    ]},
    { id: 'strategy', label: 'Strategy', icon: Layers, fields: [
      { key: 'heading', label: 'Heading', type: 'text', placeholder: 'We target properties with exceptional potential...' },
      { key: 'body', label: 'Body', type: 'textarea', placeholder: 'Our strategy focuses on...' },
      { key: 'image', label: 'Image', type: 'image' },
    ]},
    { id: 'features', label: 'Feature Cards', icon: Star, fields: [
      { key: 'items', label: 'Feature cards', type: 'features' },
    ]},
    NEWSLETTER_SECTION,
    FOOTER_SECTION,
  ],
  defaults: {
    hero: {
      heading: `About ${isRedex ? 'REDEX' : 'Honeyshares'}`,
      body: "Real estate has long been a powerful way to build wealth but getting started isn't easy. Navigating this sector often means facing high capital requirements, changing regulations, complex deal structures, and the ongoing demands of property management.\n\nOur team evaluates hundreds of deals to bring you curated opportunities in high-performing rental homes, vacation properties, and multifamily real estate. Each asset is professionally optimized and fully managed. Making this a truly hands-off investment experience.\n\nThis is real estate investing, simplified.",
      ctaText: 'Get Started',
      ctaUrl: '/register',
      image: 'https://images.unsplash.com/photo-1486325212027-8081e485255e?auto=format&fit=crop&w=800&q=80',
    },
    empowering: {
      heading: 'Empowering investors',
      statements: [
        { title: 'Accessible', description: 'Our mission is to make high-quality real estate investing simple and accessible' },
        { title: 'Transparent', description: 'We believe in transparency, performance, and shared opportunity, not complexity or exclusivity.' },
        { title: 'Consistent', description: 'Our goal is simple: help investors earn consistent, tax-efficient returns through properties they\'re proud to own.' },
      ],
    },
    strategy: {
      heading: 'We target properties with exceptional potential and long-term value.',
      body: 'Our strategy focuses on acquiring high-performing rental homes in desirable, high-demand markets. Every property is hand-selected through a data-driven process that evaluates cash flow, appreciation potential, and regulatory stability. Once acquired, each asset is professionally managed to deliver consistent income, strong appreciation, and a truly passive ownership experience.',
      image: 'https://images.unsplash.com/photo-1587049352846-4a222e784d38?auto=format&fit=crop&w=600&q=80',
    },
    features: {
      items: [
        { title: 'Simplified Access', description: 'Invest in curated real estate deals—professionally vetted and managed' },
        { title: 'Passive', description: 'We handle it all - from finances to repairs and everything in between' },
        { title: 'Earn & Grow', description: 'Quarterly cashflow from rental income plus property appreciation' },
        { title: 'Diversify', description: 'Own shares in multiple properties to spread risk and maximize returns' },
      ],
    },
    newsletter: {
      heading: 'Join our List',
      subheading: 'Add your email below to receive updates from us, including our guides to investing and notifications about new rental properties & markets.',
    },
    footer: {
      disclaimer: 'Investing in real estate involves risk, including loss of principal. Past performance does not guarantee future results. Securities offered through registered broker-dealer. This is not an offer to sell securities.',
    },
  },
};

/* ═══════════════════════════════════════════════════════════════
 *  TEAM PAGE
 * ═══════════════════════════════════════════════════════════════ */

const TEAM_TEMPLATE: SiteTemplate = {
  id: 'team',
  label: 'Team',
  description: 'Team member profiles, leadership, and bios',
  icon: Users,
  sections: [
    NAV_SECTION,
    { id: 'hero', label: 'Hero', icon: Star, fields: [
      { key: 'heading', label: 'Heading', type: 'text', placeholder: 'Our Team' },
      { key: 'subheading', label: 'Subheading', type: 'textarea', placeholder: 'Meet the people behind the platform' },
    ]},
    { id: 'leadership', label: 'Leadership', icon: Users, fields: [
      { key: 'heading', label: 'Heading', type: 'text', placeholder: 'Leadership' },
      { key: 'members', label: 'Team members', type: 'teamMembers' },
    ]},
    { id: 'team', label: 'Full Team', icon: Users, fields: [
      { key: 'heading', label: 'Heading', type: 'text', placeholder: 'The Team' },
      { key: 'members', label: 'Team members', type: 'teamMembers' },
    ]},
    { id: 'cta', label: 'Join the Team', icon: Megaphone, fields: [
      { key: 'heading', label: 'Heading', type: 'text', placeholder: 'Join Our Team' },
      { key: 'body', label: 'Body', type: 'textarea' },
      { key: 'ctaText', label: 'CTA button', type: 'text', placeholder: 'View Open Positions' },
      { key: 'ctaUrl', label: 'CTA URL', type: 'url', placeholder: '/careers' },
    ]},
    FOOTER_SECTION,
  ],
  defaults: {
    hero: { heading: 'Our Team', subheading: 'Meet the people building the future.' },
    leadership: { heading: 'Leadership', members: [] },
    team: { heading: 'The Team', members: [] },
    cta: { heading: 'Join Our Team', body: "We're always looking for talented people.", ctaText: 'View Open Positions', ctaUrl: '/careers' },
  },
};

/* ═══════════════════════════════════════════════════════════════
 *  MARKETING FUNNEL
 * ═══════════════════════════════════════════════════════════════ */

const FUNNEL_TEMPLATE: SiteTemplate = {
  id: 'funnel',
  label: 'Marketing Funnel',
  description: 'Lead capture page with benefits, social proof, and CTA',
  icon: Megaphone,
  sections: [
    { id: 'hero', label: 'Hero', icon: Star, fields: [
      { key: 'heading', label: 'Heading', type: 'text', placeholder: 'Exclusive Investment Opportunity' },
      { key: 'subheading', label: 'Subheading', type: 'textarea' },
      { key: 'ctaText', label: 'CTA button', type: 'text', placeholder: 'Get Early Access' },
      { key: 'ctaUrl', label: 'CTA URL', type: 'url', placeholder: '/register' },
      { key: 'backgroundImage', label: 'Background image', type: 'image' },
    ]},
    { id: 'benefits', label: 'Benefits', icon: Star, fields: [
      { key: 'heading', label: 'Heading', type: 'text', placeholder: 'Why Invest With Us' },
      { key: 'items', label: 'Benefit items', type: 'features' },
    ]},
    { id: 'socialProof', label: 'Social Proof', icon: Users, fields: [
      { key: 'heading', label: 'Heading', type: 'text', placeholder: 'Trusted By Investors' },
      { key: 'stats', label: 'Stats', type: 'stats' },
    ]},
    { id: 'urgency', label: 'Urgency / CTA', icon: Megaphone, fields: [
      { key: 'heading', label: 'Heading', type: 'text', placeholder: "Don't Miss Out" },
      { key: 'body', label: 'Body', type: 'textarea' },
      { key: 'ctaText', label: 'CTA button', type: 'text', placeholder: 'Reserve Your Spot' },
      { key: 'ctaUrl', label: 'CTA URL', type: 'url', placeholder: '/register' },
    ]},
    NEWSLETTER_SECTION,
  ],
  defaults: {
    hero: { heading: 'Exclusive Investment Opportunity', subheading: 'Limited spots for accredited investors', ctaText: 'Get Early Access', ctaUrl: '/register' },
    benefits: { heading: 'Why Invest With Us', items: [
      { title: 'Higher Returns', description: 'Target IRR significantly above market average' },
      { title: 'Full Transparency', description: 'Real-time reporting on every asset' },
    ]},
    socialProof: { heading: 'Trusted By Investors', stats: [{ label: 'Capital Deployed', value: '$10M+' }, { label: 'Active Investors', value: '500+' }] },
    urgency: { heading: "Don't Miss Out", body: 'Limited allocation remaining.', ctaText: 'Reserve Your Spot', ctaUrl: '/register' },
    newsletter: { heading: 'Stay Updated', subheading: 'Be the first to know about new opportunities' },
  },
};

/* ═══════════════════════════════════════════════════════════════
 *  CONTACT PAGE
 * ═══════════════════════════════════════════════════════════════ */

const CONTACT_TEMPLATE: SiteTemplate = {
  id: 'contact',
  label: 'Contact',
  description: 'Contact form and support info',
  icon: Phone,
  sections: [
    NAV_SECTION,
    { id: 'hero', label: 'Hero', icon: Star, fields: [
      { key: 'heading', label: 'Heading', type: 'text', placeholder: 'Get In Touch' },
      { key: 'subheading', label: 'Subheading', type: 'textarea' },
    ]},
    { id: 'contactInfo', label: 'Contact Info', icon: Phone, fields: [
      { key: 'email', label: 'Email', type: 'text', placeholder: 'hello@example.com' },
      { key: 'phone', label: 'Phone', type: 'text', placeholder: '+1 (555) 000-0000' },
      { key: 'address', label: 'Address', type: 'textarea', placeholder: '123 Main St, City, State' },
    ]},
    FOOTER_SECTION,
  ],
  defaults: {
    hero: { heading: 'Get In Touch', subheading: 'Reach out to our team.' },
    contactInfo: { email: isRedex ? 'hello@redexct.com' : 'hello@honeyshares.com' },
  },
};

/* ═══════════════════════════════════════════════════════════════
 *  GENERIC / CUSTOM PAGE
 * ═══════════════════════════════════════════════════════════════ */

const GENERIC_TEMPLATE: SiteTemplate = {
  id: 'generic',
  label: 'Custom Page',
  description: 'Flexible page with hero, content, features, and CTA',
  icon: Globe,
  sections: [
    NAV_SECTION,
    { id: 'hero', label: 'Hero', icon: Star, fields: [
      { key: 'heading', label: 'Heading', type: 'text', placeholder: 'Page Title' },
      { key: 'subheading', label: 'Subheading', type: 'textarea' },
      { key: 'backgroundImage', label: 'Background image', type: 'image' },
    ]},
    { id: 'content1', label: 'Content Section 1', icon: FileText, fields: [
      { key: 'heading', label: 'Heading', type: 'text' },
      { key: 'body', label: 'Body', type: 'textarea' },
      { key: 'image', label: 'Image', type: 'image' },
    ]},
    { id: 'content2', label: 'Content Section 2', icon: FileText, fields: [
      { key: 'heading', label: 'Heading', type: 'text' },
      { key: 'body', label: 'Body', type: 'textarea' },
      { key: 'image', label: 'Image', type: 'image' },
    ]},
    { id: 'features', label: 'Features', icon: Star, fields: [
      { key: 'heading', label: 'Heading', type: 'text' },
      { key: 'items', label: 'Feature items', type: 'features' },
    ]},
    { id: 'cta', label: 'Call to Action', icon: Megaphone, fields: [
      { key: 'heading', label: 'Heading', type: 'text', placeholder: 'Ready to get started?' },
      { key: 'body', label: 'Body', type: 'textarea' },
      { key: 'ctaText', label: 'CTA button', type: 'text', placeholder: 'Get Started' },
      { key: 'ctaUrl', label: 'CTA URL', type: 'url', placeholder: '/register' },
    ]},
    FOOTER_SECTION,
  ],
  defaults: {},
};

/* ═══════════════════════════════════════════════════════════════
 *  HIVE FUND PAGE — matches HiveFund.tsx
 * ═══════════════════════════════════════════════════════════════ */

const HIVEFUND_TEMPLATE: SiteTemplate = {
  id: 'hivefund',
  label: 'Hive Fund',
  description: 'Investment fund page with hero, strategy, benefits, documents, financials',
  icon: Building2,
  sections: [
    NAV_SECTION,
    { id: 'hero', label: 'Hero Banner', icon: Star, fields: [
      { key: 'heading', label: 'Heading', type: 'text', placeholder: 'The Hive Fund' },
      { key: 'subheading', label: 'Subheading', type: 'textarea', placeholder: 'Invest in a diversified portfolio...' },
      { key: 'body', label: 'Body text', type: 'textarea', placeholder: 'Access institutional-quality real estate...' },
      { key: 'ctaText', label: 'CTA button text', type: 'text', placeholder: 'Request Access' },
      { key: 'secondaryCtaText', label: 'Secondary CTA text', type: 'text', placeholder: 'Invest Now' },
    ]},
    { id: 'about', label: 'About the Fund', icon: Info, fields: [
      { key: 'heading', label: 'Heading', type: 'text', placeholder: 'About The Hive Fund' },
      { key: 'body', label: 'Body paragraphs', type: 'textarea', placeholder: 'The Hive Fund offers investors...' },
      { key: 'strategyHeading', label: 'Strategy heading', type: 'text', placeholder: 'Investment Strategy' },
      { key: 'strategyIntro', label: 'Strategy intro', type: 'textarea', placeholder: 'Our strategy is designed...' },
      { key: 'strategyPoints', label: 'Strategy points', type: 'richlist' },
    ]},
    { id: 'markets', label: 'Markets', icon: Globe, fields: [
      { key: 'heading', label: 'Markets heading', type: 'text', placeholder: 'Markets' },
      { key: 'body', label: 'Markets intro', type: 'textarea', placeholder: 'Building foundation in Tampa Bay...' },
      { key: 'items', label: 'Market highlights', type: 'features' },
    ]},
    { id: 'whyWeWin', label: 'Why We Win', icon: Shield, fields: [
      { key: 'heading', label: 'Heading', type: 'text', placeholder: 'Why We Win' },
      { key: 'body', label: 'Intro text', type: 'textarea', placeholder: 'Our competitive edge...' },
      { key: 'items', label: 'Advantages', type: 'features' },
    ]},
    { id: 'benefits', label: 'Benefits', icon: Star, fields: [
      { key: 'heading', label: 'Heading', type: 'text', placeholder: 'Benefits Overview' },
      { key: 'subheading', label: 'Subheading', type: 'textarea', placeholder: 'Passive income, appreciation, and tax efficiency' },
      { key: 'items', label: 'Benefits', type: 'features' },
    ]},
    { id: 'keyTerms', label: 'Key Terms', icon: Layers, fields: [
      { key: 'heading', label: 'Heading', type: 'text', placeholder: 'Key Investment Terms' },
      { key: 'items', label: 'Terms', type: 'stats' },
    ]},
    { id: 'howItWorks', label: 'How It Works', icon: Layers, fields: [
      { key: 'heading', label: 'Heading', type: 'text', placeholder: 'How It Works' },
      { key: 'subheading', label: 'Subheading', type: 'textarea', placeholder: 'Your journey from onboarding...' },
      { key: 'steps', label: 'Steps', type: 'richlist' },
    ]},
    { id: 'cta', label: 'Call to Action', icon: Megaphone, fields: [
      { key: 'heading', label: 'Heading', type: 'text', placeholder: 'Ready to Join The Hive?' },
      { key: 'body', label: 'Body', type: 'textarea', placeholder: 'Start building passive income...' },
      { key: 'ctaText', label: 'CTA button text', type: 'text', placeholder: 'Request Access' },
    ]},
    FOOTER_SECTION,
  ],
  defaults: {
    hero: {
      heading: 'The Hive Fund',
      subheading: 'Invest in a diversified portfolio of premium vacation rental properties',
      body: 'Access institutional-quality real estate investments with quarterly distributions, professional management, and growth potential in high-demand markets.',
      ctaText: 'Request Access',
      secondaryCtaText: 'Invest Now',
    },
    about: {
      heading: 'About The Hive Fund',
      body: 'The Hive Fund offers investors the opportunity to participate in a diversified portfolio of short-term rental properties across high-growth markets in the Southeast. By investing in the Hive Fund, investors gain exposure to multiple properties and markets through a single investment combining the stability of real estate with the income potential of hospitality assets.\n\nNew acquisitions will be added over time, expanding diversification and optimizing returns as the portfolio grows. The Hive Fund is structured as a private real estate investment fund under Regulation D 506(c) and is professionally managed by our team, leveraging data-driven analysis, value-add strategies, and hands-on operational expertise to maximize performance.',
      strategyHeading: 'Investment Strategy',
      strategyIntro: 'Our strategy is designed to generate steady income and long-term appreciation by acquiring high-performing short-term rental (STR) properties across the Southeast and beyond.',
      strategyPoints: [
        { title: 'Market Focus', description: 'Focus on mid- to high-end single-family and small multifamily homes in tourism-backed, high-growth markets' },
        { title: 'Value-Add', description: 'Light to moderate renovations and premium amenities unlock significant value and sustained cash flow' },
        { title: 'Fund Structure', description: 'Consolidate multiple assets under one fund structure for operational efficiencies and reduced overhead' },
        { title: 'Professional Management', description: 'Professional management with advanced pricing technology ensuring optimal occupancy and revenue' },
      ],
    },
    markets: {
      heading: 'Markets',
      body: "The Hive Fund is building its foundation in the Tampa Bay metro while actively expanding across the Southeast's strongest leisure and migration corridors.",
      items: [
        { title: 'Population & Job Growth', description: 'Fueling sustained rental demand' },
        { title: 'Tourism-Driven Economies', description: 'Supporting consistent occupancy and premium rates' },
        { title: 'STR-Friendly Policies', description: 'Providing operational stability' },
        { title: 'Competitive Landscape', description: 'Limited institutional saturation' },
      ],
    },
    whyWeWin: {
      heading: 'Why We Win',
      body: 'Our competitive edge comes from combining hyper-local expertise, exclusive deal flow, and a proven operational playbook.',
      items: [
        { title: 'Hyper-Local Advantage', description: "We're based in Tampa, giving us boots-on-the-ground insight into the best-performing neighborhoods and micro-markets in Florida." },
        { title: 'Established Relationships', description: 'Direct partnerships with local brokers, contractors, and maintenance mean early access to off-market deals and faster, more reliable renovations.' },
        { title: 'Below-Market Acquisitions', description: "We source off-market deals you won't see on Zillow and have wholesale property management rates at a 50-65% discount." },
        { title: 'Proven Operations Playbook', description: 'Our existing portfolio includes 27 STRs in Florida with systems that consistently deliver 70%+ occupancy and premium nightly rates.' },
        { title: 'Tax-Advantaged Structure', description: 'Investors benefit from accelerated depreciation, often offsetting a large portion of year-one income.' },
        { title: 'Defined Value-Add & Exit Strategy', description: 'Every acquisition is chosen for immediate cash flow and a clear refinance or sale opportunity within 5-7 years.' },
      ],
    },
    benefits: {
      heading: 'Benefits Overview',
      subheading: 'Passive income, appreciation, and tax efficiency - all in one investment.',
      items: [
        { title: 'Cash Flow', description: 'Investors benefit from targeted 12% annual cash flow, distributed quarterly. Each property generates steady income through premium short-term rental operations.' },
        { title: 'Appreciation', description: 'Participate in equity growth with targeted 16-18% IRR, structured around an 8% preferred return followed by a 70/30 profit split (LP/GP).' },
        { title: 'Tax Advantages', description: 'Benefit from depreciation, cost segregation studies, deductible mortgage interest, property taxes, and operating expense write-offs.' },
        { title: 'Leverage Without Liability', description: 'Participate in leveraged real estate without personal debt exposure. All financing secured through established lending partners.' },
        { title: 'Diversification', description: 'Gain balanced exposure to multiple markets across Florida, the Southeast, and beyond, each selected for tourism demand and growth potential.' },
      ],
    },
    keyTerms: {
      heading: 'Key Investment Terms',
      items: [
        { label: 'Preferred Return', value: '8%' },
        { label: 'Profit Split (LP/GP)', value: '70/30' },
        { label: 'Target IRR Range', value: '16-18%' },
        { label: 'Target Annual Cash Flow', value: '12%' },
      ],
    },
    howItWorks: {
      heading: 'How It Works',
      subheading: 'Your journey from onboarding to earning quarterly dividends',
      steps: [
        { title: 'Simple Onboarding', description: 'No loans or property hunting. Just provide key documents and complete KYC verification.' },
        { title: 'Fund Your Investment', description: 'Link your bank account or use crypto. Get diversified across high-performance short-term rentals.' },
        { title: 'Quarterly Cashflow', description: 'Distributions begin in 3-6 months. Collect dividends straight to your account every quarter.' },
        { title: 'The Exit', description: 'When properties sell, profits are distributed back to investors beyond quarterly cash flow.' },
      ],
    },
    cta: {
      heading: 'Ready to Join The Hive?',
      body: 'Start building passive income with our diversified short-term rental portfolio. First round of The Hive Fund is now open for investment.',
      ctaText: 'Request Access',
    },
    footer: {
      disclaimer: 'The Hive Fund is a Reg D offering. This is not an offer to sell securities. All investments carry risk. Past performance is not indicative of future results.',
    },
  },
};

/* ─── Registry ────────────────────────────────────────────────── */

export const SITE_TEMPLATES: SiteTemplate[] = [
  LANDING_TEMPLATE,
  HIVEFUND_TEMPLATE,
  ABOUT_TEMPLATE,
  TEAM_TEMPLATE,
  FUNNEL_TEMPLATE,
  CONTACT_TEMPLATE,
  GENERIC_TEMPLATE,
  // Additional templates below
  {
    id: 'pricing',
    label: 'Pricing',
    description: 'Pricing plans, tiers, and comparisons',
    icon: CreditCard,
    sections: [
      NAV_SECTION,
      { id: 'hero', label: 'Hero', icon: Star, fields: [
        { key: 'heading', label: 'Heading', type: 'text', placeholder: 'Pricing' },
        { key: 'subheading', label: 'Subheading', type: 'textarea', placeholder: 'Simple, transparent pricing' },
      ]},
      { id: 'plans', label: 'Plans', icon: CreditCard, fields: [
        { key: 'items', label: 'Pricing plans', type: 'features' },
      ]},
      { id: 'faq', label: 'FAQ', icon: HelpCircle, fields: [
        { key: 'heading', label: 'Heading', type: 'text', placeholder: 'Common Questions' },
        { key: 'items', label: 'FAQ items', type: 'faq' },
      ]},
      FOOTER_SECTION,
    ],
    defaults: {
      hero: { heading: 'Pricing', subheading: 'Transparent pricing for every stage of growth' },
      plans: { items: [
        { title: 'Starter', description: 'For emerging operators — core capital infrastructure' },
        { title: 'Growth', description: 'For scaling operators — full suite with white-label' },
        { title: 'Enterprise', description: 'Custom infrastructure for institutional operators' },
      ]},
      faq: { heading: 'Common Questions', items: [] },
    },
  },
  {
    id: 'legal',
    label: 'Legal / Terms',
    description: 'Terms of service, privacy policy, or legal pages',
    icon: FileText,
    sections: [
      NAV_SECTION,
      { id: 'hero', label: 'Hero', icon: Star, fields: [
        { key: 'heading', label: 'Page title', type: 'text', placeholder: 'Terms of Service' },
        { key: 'subheading', label: 'Subtitle', type: 'text', placeholder: 'Last updated: March 2026' },
      ]},
      { id: 'content', label: 'Content', icon: FileText, fields: [
        { key: 'body', label: 'Full content', type: 'textarea', placeholder: 'Enter the full legal text here...' },
      ]},
      FOOTER_SECTION,
    ],
    defaults: {
      hero: { heading: 'Terms of Service', subheading: 'Last updated: March 2026' },
      content: { body: '' },
    },
  },
];

export function getTemplate(id: string): SiteTemplate | undefined {
  return SITE_TEMPLATES.find(t => t.id === id);
}

export function getTemplateOrDefault(id: string | undefined): SiteTemplate {
  return getTemplate(id || '') || GENERIC_TEMPLATE;
}
